import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { ClerkProvider, SignIn, SignUp, useAuth } from '@clerk/clerk-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { WebSocketProvider } from './contexts/WebSocketContext';

// Components
import Dashboard from './pages/Dashboard';
import BattleRoom from './pages/BattleRoom.js';
import RulesetEditor from './pages/RulesetEditor';
import UpgradePage from './pages/UpgradePage';
import LegalAndSourcesPage from './pages/LegalAndSourcesPage';
import MonsterSheetViewer from './components/MonsterSheetViewer/MonsterSheetViewer';
import MonsterEditor from './components/MonsterEditor';
import CharacterSheetEditor from './components/CharacterSheetEditor';
import MonsterSheetEditor from './components/MonsterSheetEditor';
import CharacterConsistencyTest from './components/CharacterConsistencyTest';
import AppLegalFooter from './components/AppLegalFooter';
import BattleRoomTestHarnessPage from './testing/BattleRoomTestHarnessPage';
import GmaBattleRoomBridgePage from './testing/GmaBattleRoomBridgePage';
import MonsterEditorTestPage from './testing/MonsterEditorTestPage';
import MonsterViewerTestPage from './testing/MonsterViewerTestPage';
import CharacterInventoryTestPage from './testing/CharacterInventoryTestPage';
import CharacterPrintTestPage from './testing/CharacterPrintTestPage';
import CharacterPresentationTestPage from './testing/CharacterPresentationTestPage';
import { MONSTER_EDITOR_TEST_ROUTE, MONSTER_VIEWER_TEST_ROUTE } from './testing/monsterUIHarnessFixtures';
import { getBackendEntitlements } from './services/EntitlementService';
import { clearResolvedEntitlements } from './utils/runtimeTier';
import { getCanonicalHostRedirect } from './utils/authOrigin';
import {
  buildAuthRoute,
  buildLocationRedirectTarget,
  getCurrentOriginAllowlist,
  getCurrentProtocolAllowlist,
  getRedirectFromSearch
} from './utils/authRedirect';
// Styles
import './index.css';
import './battleroom.css';
import './styles/auth.css';

const queryClient = new QueryClient();
const CLERK_PLACEHOLDER_KEY = 'your-clerk-publishable-key';
const BATTLE_ROOM_TEST_ROUTE = '/__test__/battle-room';
const CHARACTER_INVENTORY_TEST_ROUTE = '/__test__/character-inventory';
const CHARACTER_PRINT_TEST_ROUTE = '/__test__/character-print';
const CHARACTER_PRESENTATION_TEST_ROUTE = '/__test__/character-presentation';
const GMA_BATTLE_ROOM_ROUTE = '/__gma__/battle-room';
const TEST_ROUTES_ENABLED = process.env.REACT_APP_ENABLE_TEST_ROUTES === 'true';
const GMA_BRIDGE_ENABLED = process.env.REACT_APP_ENABLE_GMA_BRIDGE === 'true';
const CLERK_PUBLISHABLE_KEY_PATTERN = /^pk_(?:test|live)_[A-Za-z0-9_-]+$/;
const clerkLocalization = {
  signIn: {
    start: {
      title: 'Sign in to Virtual Combat Simulator',
      subtitle: 'Welcome back! Please sign in to continue'
    }
  },
  signUp: {
    start: {
      title: 'Create your Virtual Combat Simulator account',
      subtitle: 'Welcome! Please sign up to continue'
    }
  }
};

// date_of_change: 2026-04-27 - Generalized from battle-room-only to any /__test__/* path
const isTestPathActive = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  const pathname = window.location.pathname || '';
  const bridgeQuery = new URLSearchParams(window.location.search || '').get('gmaBridge') === '1';
  return pathname.startsWith('/__test__/') || (GMA_BRIDGE_ENABLED && (pathname.startsWith('/__gma__/') || bridgeQuery));
};

const clerkAuthAppearance = {
  layout: {
    logoPlacement: 'none',
    socialButtonsVariant: 'blockButton'
  },
  variables: {
    colorPrimary: 'var(--ui-gold)',
    colorText: 'var(--ui-dark-text)',
    colorTextSecondary: 'var(--ui-dark-muted)',
    colorBackground: 'var(--ui-dark-panel-strong)',
    colorInputBackground: 'var(--ui-dark-panel)',
    colorInputText: 'var(--ui-dark-text)',
    colorDanger: 'var(--ui-danger-text)',
    borderRadius: 'var(--ui-radius-card)',
    fontFamily: 'var(--ui-font-interface)',
    fontFamilyButtons: 'var(--ui-font-interface)'
  },
  elements: {
    rootBox: 'auth-clerk-root',
    cardBox: 'auth-clerk-card-box',
    card: 'auth-clerk-card',
    headerTitle: 'auth-clerk-header-title',
    headerSubtitle: 'auth-clerk-header-subtitle',
    socialButtons: 'auth-clerk-social-buttons',
    socialButtonsBlockButton: 'auth-clerk-social-button',
    socialButtonsProviderIcon: 'auth-clerk-social-provider-icon',
    dividerRow: 'auth-clerk-divider-row',
    dividerText: 'auth-clerk-divider-text',
    dividerLine: 'auth-clerk-divider-line',
    formFieldLabel: 'auth-clerk-field-label',
    formFieldInput: 'auth-clerk-field-input',
    formButtonPrimary: 'auth-clerk-primary-button',
    footer: 'auth-clerk-footer',
    footerAction: 'auth-clerk-footer-action',
    footerActionText: 'auth-clerk-footer-text',
    footerActionLink: 'auth-clerk-footer-link',
    formResendCodeLink: 'auth-clerk-inline-link',
    headerBackLink: 'auth-clerk-inline-link',
    formFieldAction: 'auth-clerk-inline-link',
    alertText: 'auth-clerk-alert-text',
    formFieldErrorText: 'auth-clerk-error-text',
    otpCodeFieldInput: 'auth-clerk-otp-input'
  }
};

const AuthStatusScreen = ({ label, isError = false }) => (
  <main className="auth-status ui-fantasy-screen" aria-busy={!isError}>
    <section
      className={`auth-status__panel ui-fantasy-panel${isError ? ' auth-status__panel--error' : ''}`}
      role={isError ? 'alert' : 'status'}
      aria-live={isError ? 'assertive' : 'polite'}
    >
      <p className="auth-status__kicker ui-fantasy-kicker">Secure table access</p>
      <h1 className="auth-status__title ui-fantasy-title">Virtual Combat Simulator</h1>
      <p className="auth-status__message">{label}</p>
      {!isError && (
        <div className="auth-status__progress" aria-hidden="true">
          <span />
        </div>
      )}
      {isError && (
        <p className="auth-status__support">
          Refresh the page and try again. If the issue continues, contact{' '}
          <a href="mailto:info@sixsmithgames.com">info@sixsmithgames.com</a>.
        </p>
      )}
    </section>
  </main>
);

const AuthLoadingScreen = ({ label }) => <AuthStatusScreen label={label} />;

const CanonicalHostRedirect = () => {
  const redirectUrl = getCanonicalHostRedirect();

  useEffect(() => {
    if (redirectUrl) {
      window.location.replace(redirectUrl);
    }
  }, [redirectUrl]);

  if (!redirectUrl) {
    return null;
  }

  return <AuthLoadingScreen label="Opening the secure VCS address..." />;
};

const ClerkConfigurationError = () => (
  <AuthStatusScreen
    isError
    label="We hit a sign-in problem and could not open the game gate right now."
  />
);

class ClerkConfigurationBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    return this.state.failed ? <ClerkConfigurationError /> : this.props.children;
  }
}

const AuthScreen = ({ mode }) => {
  const { isLoaded, isSignedIn } = useAuth();
  const location = useLocation();
  const redirectUrl = getRedirectFromSearch(location.search);
  const signInUrl = buildAuthRoute('/sign-in', redirectUrl);
  const signUpUrl = buildAuthRoute('/sign-up', redirectUrl);

  if (!isLoaded) {
    return <AuthLoadingScreen label="Opening the guild gate..." />;
  }

  if (isSignedIn) {
    return <Navigate to={redirectUrl} replace />;
  }

  return (
    <div className="auth-screen ui-fantasy-screen">
      <div className="auth-screen__card">
        <div className="auth-screen__form">
          {mode === 'sign-in' ? (
            <SignIn
              routing="path"
              path="/sign-in"
              signUpUrl={signUpUrl}
              fallbackRedirectUrl={redirectUrl}
              signUpFallbackRedirectUrl={redirectUrl}
              appearance={clerkAuthAppearance}
            />
          ) : (
            <SignUp
              routing="path"
              path="/sign-up"
              signInUrl={signInUrl}
              fallbackRedirectUrl={redirectUrl}
              signInFallbackRedirectUrl={redirectUrl}
              appearance={clerkAuthAppearance}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const RequireAuth = () => {
  const { isLoaded, isSignedIn } = useAuth();
  const location = useLocation();

  if (!isLoaded) {
    return <AuthLoadingScreen label="Checking your table pass..." />;
  }

  if (!isSignedIn) {
    const redirectUrl = buildLocationRedirectTarget(location);
    return <Navigate to={buildAuthRoute('/sign-in', redirectUrl)} replace />;
  }

  return <Outlet />;
};

const EntitlementBootstrap = () => {
  const { isLoaded, isSignedIn } = useAuth();

  useEffect(() => {
    if (!isLoaded) {
      return;
    }

    if (!isSignedIn) {
      clearResolvedEntitlements();
      return;
    }

    let cancelled = false;
    (async () => {
      const snapshot = await getBackendEntitlements({ forceRefresh: true });
      if (!cancelled && snapshot?.userTier) {
        console.debug('[Entitlements] Synced backend tier:', snapshot.userTier);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isLoaded, isSignedIn]);

  return null;
};

const ProtectedLayout = () => (
  <div className="min-h-screen flex flex-col" style={{ background: 'var(--game-bg)', color: 'var(--game-text)' }}>
    <EntitlementBootstrap />
    <div className="flex-1">
      <Outlet />
    </div>
    <AppLegalFooter />
  </div>
);

const TestRoutesApp = () => (
  <QueryClientProvider client={queryClient}>
    <BrowserRouter>
      <Routes>
        <Route path={BATTLE_ROOM_TEST_ROUTE} element={<BattleRoomTestHarnessPage />} />
        {GMA_BRIDGE_ENABLED && <Route path={GMA_BATTLE_ROOM_ROUTE} element={<GmaBattleRoomBridgePage />} />}
        {GMA_BRIDGE_ENABLED && <Route path="/" element={<GmaBattleRoomBridgePage />} />}
        {/* date_of_change: 2026-04-27 - Monster UI visual-test harness routes */}
        <Route path={`${MONSTER_EDITOR_TEST_ROUTE}/:templateId`} element={<MonsterEditorTestPage />} />
        <Route path={MONSTER_EDITOR_TEST_ROUTE} element={<Navigate to={`${MONSTER_EDITOR_TEST_ROUTE}/new`} replace />} />
        <Route path={MONSTER_VIEWER_TEST_ROUTE} element={<MonsterViewerTestPage />} />
        {/* date_of_change: 2026-07-29 - Character inventory wide/narrow visual verification harness */}
        <Route path={CHARACTER_INVENTORY_TEST_ROUTE} element={<CharacterInventoryTestPage />} />
        {/* date_of_change: 2026-08-02 - Static print-document wide/narrow and PDF stress harness */}
        <Route path={CHARACTER_PRINT_TEST_ROUTE} element={<CharacterPrintTestPage />} />
        <Route path={CHARACTER_PRESENTATION_TEST_ROUTE} element={<CharacterPresentationTestPage />} />
        <Route path="*" element={<Navigate to={BATTLE_ROOM_TEST_ROUTE} replace />} />
      </Routes>
    </BrowserRouter>
  </QueryClientProvider>
);

const App = () => {
  if (TEST_ROUTES_ENABLED && isTestPathActive()) {
    return <TestRoutesApp />;
  }

  // date_of_change: 2026-07-27 - Redirect the retired Vercel alias before Clerk initializes because Clerk rejects that origin.
  const canonicalHostRedirect = getCanonicalHostRedirect();
  if (canonicalHostRedirect) {
    return <CanonicalHostRedirect />;
  }

  const clerkPublishableKey = String(process.env.REACT_APP_CLERK_PUBLISHABLE_KEY || '').trim();
  // WHY/WHAT: reject empty and placeholder-shaped configuration before Clerk can
  // throw during provider render; the boundary still contains vendor validation errors.
  // date_of_change: 2026-09-14
  const hasClerkConfiguration = clerkPublishableKey !== CLERK_PLACEHOLDER_KEY
    && CLERK_PUBLISHABLE_KEY_PATTERN.test(clerkPublishableKey);

  if (!hasClerkConfiguration) {
    return <ClerkConfigurationError />;
  }

  return (
    <ClerkConfigurationBoundary>
      {/* WHY/WHAT: Clerk Core 2 moves redirect and sign-out defaults to the provider.
          Keep every default local and let the preflight-vetted redirect_url win when present.
          date_of_change: 2026-09-14 */}
      <ClerkProvider
        publishableKey={clerkPublishableKey}
        localization={clerkLocalization}
        signInUrl="/sign-in"
        signUpUrl="/sign-up"
        signInFallbackRedirectUrl="/"
        signUpFallbackRedirectUrl="/"
        afterSignOutUrl="/"
        allowedRedirectOrigins={getCurrentOriginAllowlist(window)}
        allowedRedirectProtocols={getCurrentProtocolAllowlist(window)}
      >
        <QueryClientProvider client={queryClient}>
          <WebSocketProvider>
            <BrowserRouter>
            <Routes>
              <Route path="/sign-in/*" element={<AuthScreen mode="sign-in" />} />
              <Route path="/sign-up/*" element={<AuthScreen mode="sign-up" />} />
              {/* date_of_change: 2026-04-22
                  rule_reference: Marketing-funnel conversion - /character/edit/new is served
                  publicly so anonymous visitors arriving from sixsmithgames.com/apps/virtual-combat-simulator
                  can try the character sheet editor without signing in. CharacterSheetEditor
                  soft-gates Save Character, Upload Image, Choose Existing, and the Dashboard
                  button internally when the viewer is anonymous and routes them to
                  /sign-in?redirect_url=... so they come back after signing in. Any other
                  /character/edit/:templateId route still resolves inside RequireAuth below. */}
              <Route element={<ProtectedLayout />}>
                <Route path="/character/new" element={<Navigate to="/character/edit/new" replace />} />
                <Route path="/character/edit" element={<Navigate to="/character/edit/new" replace />} />
                <Route path="/character/edit/new" element={<CharacterSheetEditor />} />
              </Route>
              <Route element={<RequireAuth />}>
                <Route element={<ProtectedLayout />}>
                  <Route path="/" element={<Dashboard />} />
                  <Route path="/battle" element={<BattleRoom />}/>
                  <Route path="/battle/:sessionId" element={<BattleRoom />}/>
                  <Route path="/battleroom" element={<BattleRoom />}/>
                  <Route path="/battleroom/:sessionId" element={<BattleRoom />}/>
                    <Route path="/ruleset" element={<RulesetEditor />}/>
                    <Route path="/ruleset/new" element={<RulesetEditor />}/>
                    <Route path="/ruleset/:id" element={<RulesetEditor />}/>
                  <Route path="/upgrade" element={<UpgradePage />} />
                  <Route path="/legal/sources" element={<LegalAndSourcesPage />} />
                  {/* date_of_change: 2025-10-18 - Unify character view/edit to CharacterSheetEditor with mode query param */}
                  <Route path="/character/:templateId" element={<CharacterSheetEditor />} />
                  <Route path="/character/edit/:templateId" element={<CharacterSheetEditor />} />
                  <Route path="/character/sheet-edit/:templateId" element={<CharacterSheetEditor />} />
                  <Route path="/monster/:id" element={<MonsterSheetViewer />}/>
                  <Route path="/monster/edit/:templateId" element={<MonsterEditor />} />
                  <Route path="/monster/sheet-edit/:templateId" element={<MonsterSheetEditor />} />
                  <Route path="/test/character-consistency" element={<CharacterConsistencyTest />} />
                </Route>
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            </BrowserRouter>
          </WebSocketProvider>
        </QueryClientProvider>
      </ClerkProvider>
    </ClerkConfigurationBoundary>
  );
};

export default App;
